import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { getServiceForm } from 'src/app/forms/serviceForm';
import { IClient, IServiceInfoDetail } from 'src/app/models';
import { Colors, ServiceType, UserRole } from 'src/app/models/enums';
import {
  LiteralService,
  ServiceWizardService,
  StorageService,
  UsersService,
  UtilsService,
} from 'src/app/services';
import { images } from 'src/images';

@Component({
  selector: 'app-service-details',
  templateUrl: './service-details.component.html',
  styleUrl: './service-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ServiceDetailsComponent implements OnChanges, OnInit {
  @Input() serviceInfoDetail: any = {} as IServiceInfoDetail;
  @Input() newServiceObject: any = {} as IServiceInfoDetail;
  @Input() newService: boolean;
  @Input() edit: boolean;
  @Output() editEvent = new EventEmitter<any>();
  @Output() createEventEmitter = new EventEmitter<IServiceInfoDetail>();
  @Output() editEventEmitter = new EventEmitter<IServiceInfoDetail>();
  @Output() deleteEventEmitter = new EventEmitter<number>();
  @Output() closeDetails = new EventEmitter<any>();

  public expanded = false;

  public images = images;
  public serviceForm: any[];

  public formGroup: FormGroup;

  public steps = [1];
  public numberSteps = 4;
  public currentStep = 1;
  public lastStep = 4;

  public clients: IClient[] = [];

  public loaded = false;
  public userRole: UserRole;

  constructor(
    public literalService: LiteralService,
    private storageService: StorageService,
    private usersService: UsersService,
    public utilsService: UtilsService,
    public serviceWizardService: ServiceWizardService,
  ) {}

  async ngOnInit() {
    this.userRole = await this.storageService.getRole();
    await this.storageService.getClients().then((res) => {
      this.clients = JSON.parse(res) as IClient[];
    });
    await this.updateServiceForm();
  }

  async ngOnChanges() {
    if (this.newService) {
      if (this.isPristineNewService()) {
        if (this.currentStep !== 1) {
          this.steps = [1];
          this.currentStep = 1;
          this.expanded = false;
        }
      } else if (this.currentStep === 1 && this.newServiceObject?.serviceType) {
        this.currentStep = 4;
      }
    }
    this.formGroup = new FormGroup({});
    this.formGroup.setControl(
      'tag',
      new FormControl(
        this.newService
          ? this.newServiceObject.tag
          : this.serviceInfoDetail.tag,
        [Validators.required],
      ),
    );
    await this.updateServiceForm();
  }

  private isPristineNewService(): boolean {
    return (
      !this.newServiceObject?.serviceType &&
      this.newServiceObject?.clientId === 0 &&
      this.newServiceObject?.townId === 0 &&
      !this.newServiceObject?.tag
    );
  }

  get isDiscretionary(): boolean {
    const target = this.newService
      ? this.newServiceObject
      : this.serviceInfoDetail;
    return target?.serviceType === ServiceType.DISCRETIONARY;
  }

  public async updateServiceForm() {
    if (
      this.newService &&
      this.clients.length === 1 &&
      this.currentStep === 1
    ) {
      this.currentStep = 2;
      this.steps.push(2);
    }
    await getServiceForm(
      this.newService ? this.newServiceObject : this.serviceInfoDetail,
      this.newService,
      this.usersService,
      this.literalService,
      this.clients,
      this.currentStep,
      this.userRole,
    ).then((res: any) => {
      this.serviceForm = res;
      setTimeout(() => {
        this.loaded = true;
      }, 1200);
    });
  }

  close() {
    // Don't empty serviceInfoDetail/newServiceObject locally — the parent owns
    // those (passed via @Input) and will clear them in response to closeDetails.
    // Mutating them here briefly shows an empty form during the close transition.
    this.steps = [1];
    this.currentStep = 1;
    this.expanded = false;
    this.edit = false;
    this.closeDetails.emit();
  }

  onExpand() {
    this.expanded = !this.expanded;
  }

  async changeData(data: any, element?: any) {
    let valueChanged: any;
    const valueToChange = element?.edit?.valueToChange;

    if (valueToChange === 'clients') {
      this.currentStep = 2;
      this.steps.push(2);
      this.newServiceObject.clientId = data.value.id;
      valueChanged = [data.value];
    } else if (valueToChange === 'serviceType') {
      const value =
        typeof data === 'object' ? data.target || data.options?.[0] : data;
      valueChanged = this.setNormalData(value, typeof data === 'object');
      if (this.newService) {
        if (valueChanged === ServiceType.DISCRETIONARY) {
          this.newServiceObject.color = Colors.TRANSPARENT;
          this.newServiceObject.roundTrips = true;
          if (!this.newServiceObject.townId) this.newServiceObject.townId = -1;
          this.currentStep = Math.max(this.currentStep, 4);
        } else {
          if (this.newServiceObject.color === Colors.TRANSPARENT) {
            this.newServiceObject.color = Colors.NONE;
          }
          this.newServiceObject.roundTrips = false;
          this.currentStep = Math.max(this.currentStep, 3);
        }
      }
    } else if (valueToChange === 'towns') {
      this.newServiceObject.townId = data.value.id;
      valueChanged = [data.value];
      if (this.newService) {
        this.currentStep = Math.max(this.currentStep, 4);
      }
    } else if (Array.isArray(valueToChange)) {
      this.setMultiSelectData(element);
    } else {
      const value =
        typeof data === 'object' ? data.target || data.options?.[0] : data;
      valueChanged = this.setNormalData(value, typeof data === 'object');
    }

    const targetObject = this.newService
      ? this.newServiceObject
      : this.serviceInfoDetail;
    if (targetObject && valueToChange) {
      targetObject[valueToChange] = valueChanged;
      this.formGroup.get(valueToChange)?.setValue(valueChanged);
    }

    await this.updateServiceForm();
  }

  // Keystroke-level update so validations (e.g. the wizard Continue button) react without
  // leaving the field; the full changeData still runs on change to rebuild the form
  liveChangeData(data: any, element?: any) {
    const valueToChange = element?.edit?.valueToChange;
    if (!valueToChange || Array.isArray(valueToChange)) return;
    const value =
      typeof data === 'object' ? (data.target?.value ?? data) : data;
    const targetObject = this.newService
      ? this.newServiceObject
      : this.serviceInfoDetail;
    if (targetObject) {
      targetObject[valueToChange] = value;
      this.formGroup.get(valueToChange)?.setValue(value);
    }
  }

  private setNormalData(data: any, isEvent = false) {
    return isEvent ? data.value : data;
  }

  private setMultiSelectData(element: any) {
    element.edit.valueToChange.map((value: string) => {
      let checked = false;
      if (
        element.edit?.selectedValues.some(
          (selectedValue: any) => selectedValue.id === value,
        )
      ) {
        checked = true;
      }
      this.newService
        ? (this.newServiceObject[value] = checked)
        : (this.serviceInfoDetail[value] = checked);
    });
  }

  async onSubmit() {
    if (this.newService) {
      this.createEventEmitter.emit(
        this.trimStringFields(this.newServiceObject),
      );
    } else {
      this.editEventEmitter.emit(this.trimStringFields(this.serviceInfoDetail));
    }
    this.ngOnChanges();
  }

  private trimStringFields<T>(obj: T): T {
    for (const key of Object.keys(obj as Record<string, unknown>)) {
      const value = (obj as Record<string, unknown>)[key];
      if (typeof value === 'string') {
        (obj as Record<string, unknown>)[key] = value.trim();
      }
    }
    return obj;
  }
}
